import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ArrowPathIcon, ArrowUpRightIcon, ShoppingBagIcon, ClockIcon, UsersIcon, TruckIcon, BuildingStorefrontIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import Layout from '../component/Layout';
import Report from './Report';
import { brand } from '../config/brands';
import { getAllOrders, getAllOrdersByBusiness, getAllPendingOrders, getAllPendingOrdersByBusiness, getCustomers, findCustomersByBusiness, getBusiness, getAvailableDrivers } from '../adapter/api';
import './Dashboard.css';

const number = value => value == null ? '—' : value.toLocaleString();
const dateKey = date => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
const orderDate = order => {
  if (!order.date) return null;
  const date = new Date(order.date);
  return Number.isNaN(date.getTime()) ? null : date;
};
const status = order => order.orderStatus?.name || 'Unknown';
const tone = name => ({ Pending: 'orange', Confirmed: 'blue', Delivered: 'green', Completed: 'green', Cancelled: 'red' }[name] || 'neutral');

const Dashboard = () => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [businessAccount, setBusinessAccount] = useState(false);
  const [updated, setUpdated] = useState(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setErrors([]);
      try {
        const user = JSON.parse(await AsyncStorage.getItem('myUser'));
        if (!['ROLE_ADMIN', 'ROLE_BUSINESS'].includes(user?.role)) throw new Error('Session unavailable');
        const scoped = user.role === 'ROLE_BUSINESS';
        if (scoped && !user.email) throw new Error('Business account unavailable');
        if (!active) return;
        setBusinessAccount(scoped);
        const requests = scoped ? {
          orders: () => getAllOrdersByBusiness(user.email),
          pending: () => getAllPendingOrdersByBusiness(user.email),
          customers: () => findCustomersByBusiness(user.email),
        } : {
          orders: getAllOrders, pending: getAllPendingOrders, customers: getCustomers,
          businesses: getBusiness, drivers: getAvailableDrivers,
        };
        const entries = Object.entries(requests);
        const results = await Promise.allSettled(entries.map(async ([, fetch]) => {
          const response = await fetch();
          if (response?.data?.status === false || !Array.isArray(response?.data?.data)) throw new Error('Data unavailable');
          return response.data.data;
        }));
        if (!active) return;
        const next = {};
        const failed = [];
        results.forEach((result, index) => {
          const key = entries[index][0];
          if (result.status === 'fulfilled') next[key] = result.value;
          else failed.push(key);
        });
        setData(next);
        setErrors(failed);
        setUpdated(results.some(result => result.status === 'fulfilled') ? new Date() : null);
      } catch {
        if (active) { setData({}); setUpdated(null); setErrors(['account data']); }
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [refresh]);

  const orders = data.orders || [];
  const now = new Date();
  const days = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6 + index);
    return { date, count: orders.filter(order => { const created = orderDate(order); return created && dateKey(created) === dateKey(date); }).length };
  });
  const weekTotal = days.reduce((sum, day) => sum + day.count, 0);
  const max = Math.max(1, ...days.map(day => day.count));
  const statuses = Object.entries(orders.reduce((counts, order) => {
    const name = status(order); counts[name] = (counts[name] || 0) + 1; return counts;
  }, {})).sort((a, b) => b[1] - a[1]);

  const metrics = [
    { label: 'Total orders', value: data.orders?.length, note: 'All recorded orders', icon: ShoppingBagIcon, href: '/all-orders', color: 'blue' },
    { label: 'Pending orders', value: data.pending?.length, note: 'Review the order queue', icon: ClockIcon, href: '/pending-orders', color: 'orange' },
    { label: 'Customers', value: data.customers?.length, note: businessAccount ? 'Your customers' : 'Registered customers', icon: UsersIcon, href: '/customers', color: 'purple' },
    ...(!businessAccount ? [{ label: 'Available drivers', value: data.drivers?.length, note: 'Ready for deliveries', icon: TruckIcon, href: '/drivers', color: 'green' }] : []),
  ];
  const shortcuts = [
    { label: 'Manage orders', note: 'Review and update deliveries', href: '/pending-orders', icon: ShoppingBagIcon },
    { label: 'View performance', note: 'Explore sales and order history', href: '#performance', icon: ChartBarIcon },
    { label: businessAccount ? 'Manage menu' : 'Manage businesses', note: businessAccount ? 'Keep your products up to date' : 'Keep your partners up to date', href: businessAccount ? '/menu' : '/businesses', icon: BuildingStorefrontIcon },
  ];
  const empty = message => <div className="dd-empty">{loading ? 'Loading dashboard…' : message}</div>;

  return (
    <Layout>
      <div className="dd-dashboard" data-brand={brand.key} aria-busy={loading}>
        <header className="dd-header">
          <div><p className="dd-eyebrow">{brand.displayName.toUpperCase()} / OVERVIEW</p><h1>Dashboard</h1><p>Everything you need to keep your deliveries moving.</p></div>
          <div className="dd-header-actions"><span>{now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span><button onClick={() => setRefresh(value => value + 1)} disabled={loading}><ArrowPathIcon className={loading ? 'dd-spin' : ''} />{loading ? 'Refreshing' : 'Refresh'}</button></div>
        </header>
        {errors.length > 0 && <div className="dd-error" role="alert">Could not load {errors.join(', ')}. Refresh to try again. Other available data is shown below.</div>}
        <section className="dd-welcome">
          <div><span className="dd-welcome-tag">YOUR OPERATIONS AT A GLANCE</span><h2>Let’s get things moving.</h2><p>{businessAccount ? 'Stay on top of your orders, customers, and menu.' : 'Stay connected to your orders, delivery team, and business partners.'}</p><Link to="/pending-orders">Open order queue <ArrowUpRightIcon /></Link></div>
          <div className="dd-welcome-count"><span>Orders today</span><strong>{loading ? '…' : number(data.orders ? days[6].count : null)}</strong><span>Across {businessAccount ? 'your business' : 'your platform'}</span></div>
        </section>
        <section className="dd-metrics" aria-label="Key metrics">
          {metrics.map(({ label, value, note, icon: Icon, href, color }) => <Link to={href} className="dd-metric" key={label}><div className="dd-metric-top"><span className={`dd-icon ${color}`}><Icon /></span><ArrowUpRightIcon /></div><p>{label}</p><strong>{loading ? '…' : number(value)}</strong><small>{!loading && value == null ? 'Currently unavailable' : note}</small></Link>)}
        </section>
        <div className="dd-chart-grid">
          <section className="dd-panel"><div className="dd-panel-heading"><div><h2>Order activity</h2><p>Last 7 days · including today</p></div><span className="dd-total">{loading ? '…' : number(data.orders ? weekTotal : null)} orders</span></div>
            {!loading && data.orders ? <div className="dd-bars" role="img" aria-label={days.map(day => `${day.date.toLocaleDateString()}: ${day.count} orders`).join('; ')}>{days.map(day => <div className="dd-bar-column" key={dateKey(day.date)}><span>{day.count}</span><div className="dd-bar-track"><div className="dd-bar" style={{ height: `${day.count / max * 100}%` }} /></div><small>{day.date.toLocaleDateString('en-US', { weekday: 'short' })}</small></div>)}</div> : empty('Order activity is unavailable.')}
          </section>
          <section className="dd-panel"><div className="dd-panel-heading"><div><h2>Order status</h2><p>All recorded orders</p></div><Link to="/all-orders" aria-label="View all orders"><ArrowUpRightIcon /></Link></div>
            {!loading && statuses.length ? <div className="dd-status-list">{statuses.map(([name, count]) => <div key={name}><div className="dd-status-label"><span><i className={`dd-dot ${tone(name)}`} />{name}</span><strong>{number(count)} <small>{Math.round(count / orders.length * 100)}%</small></strong></div><div className="dd-status-track"><div className={tone(name)} style={{ width: `${count / orders.length * 100}%` }} /></div></div>)}</div> : empty(data.orders ? 'No orders yet. Statuses will appear here.' : 'Order statuses are unavailable.')}
          </section>
        </div>
        <section className="dd-shortcuts" aria-label="Quick actions">{shortcuts.map(({ label, note, href, icon: Icon }) => <a href={href} key={href}><span className="dd-icon blue"><Icon /></span><div><h3>{label}</h3><p>{note}</p></div><ArrowUpRightIcon /></a>)}</section>
        <Report orders={orders} userRole={businessAccount ? 'ROLE_BUSINESS' : 'ROLE_ADMIN'} loading={loading} error={!loading && !data.orders ? 'Report data is unavailable. Use Refresh above to try again.' : ''} />
        <footer className="dd-footer"><span>{updated ? `Last refreshed at ${updated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Waiting for dashboard data'}</span>{!businessAccount && <Link to="/businesses">{loading ? '…' : number(data.businesses?.length)} business partners <ArrowUpRightIcon /></Link>}</footer>
      </div>
    </Layout>
  );
};
export default Dashboard;
